import { QuoteVariableType } from '@sequencehq/quote-content'
import { add, format, formatDuration } from '@sequencehq/utils/dates'
import { match } from 'ts-pattern'
import { quoteVariableLabel } from './quoteVariables.constants'
import { CubeReducerState } from 'Cube/domain/cube.domain.types'
import { formatAddress, toCurrencyName } from '@sequencehq/utils'
import { countriesAlpha2, Currency } from '@sequencehq/api'
import { getBillingAddress } from '@sequencehq/api/utils/commonModels'

export const getVariableValue =
  (queries: CubeReducerState['queries'], data: CubeReducerState['data']) =>
  (variableType: QuoteVariableType): string | undefined => {
    const value = match(variableType)
      .with('CURRENCY', () => {
        const currency = data.common.currency
        return toCurrencyName(currency as Currency)
      })
      .with('CUSTOMER_NAME', () => {
        const customerId = data.common.customerId

        if (!customerId) return undefined

        const allCustomers = data.customers

        return allCustomers[customerId]?.legalName
      })
      .with('DURATION', () => {
        const duration = queries.totalPricingDuration

        if (!duration) return 'Open ended'

        return formatDuration(
          { months: (duration.years ?? 0) * 12 + (duration.months ?? 0) },
          { format: ['months'] }
        )
      })
      .with('START_DATE', () => {
        const startDate = data.common.startDate
        if (!startDate) return undefined
        return format(startDate, 'd MMMM yyyy')
      })
      .with('END_DATE', () => {
        const startDate = data.common.startDate
        const duration = queries.totalPricingDuration

        if (!startDate || !duration) return undefined

        const endDate = add(new Date(startDate), { ...duration, days: -1 })

        return format(endDate, 'd MMMM yyyy')
      })
      .with('CUSTOMER_ADDRESS', () => {
        const customerId = data.common.customerId

        if (!customerId) return undefined

        const allCustomers = data.customers

        const address = getBillingAddress(allCustomers[customerId]?.addresses)

        if (!address) return undefined

        return formatAddress(address, false).join(', ')
      })
      .with('CUSTOMER_COUNTRY', () => {
        const customerId = data.common.customerId

        if (!customerId) return undefined

        const allCustomers = data.customers

        const countryCode = getBillingAddress(
          allCustomers[customerId]?.addresses
        )?.country

        return countryCode ? countriesAlpha2[countryCode] : undefined
      })
      .with('PAYMENT_TERMS', () => {
        const defaultDueDateDays = data.quote.defaultDueDateDays
        if (typeof defaultDueDateDays === 'undefined') return undefined
        return `Net ${defaultDueDateDays}`
      })
      .with('QUOTE_OWNER', () => {
        const createdByEmail = data.quote.createdByEmail
        if (!createdByEmail) return undefined
        return preventBlockNoteLinkedText(createdByEmail)
      })
      .with('RECIPIENTS', () => {
        const contacts = data.quote.contacts
        if (!contacts.length) return undefined
        return contacts
          .map(({ name, email }) => {
            if (name) return `${name} (${preventBlockNoteLinkedText(email)})`
            return preventBlockNoteLinkedText(email)
          })
          .join(', ')
      })
      .exhaustive()

    return value
  }

/**
 * As of BlockNote 0.41.1, inserting an custom inline block with `content: 'styled'` causes email addresses to be
 * automatically turned into links after pressing space. BlockNote does a terrible job at this (only the last email in
 * some text is turned into a link and not the entire email address is made part of the link; see
 * https://sequenceslack.slack.com/archives/C06P5096R1D/p1758806538654409 for an example).
 *
 * By padding the email with the invisible character U+200E, it tricks BlockNote into not turning this text into links.
 */
const preventBlockNoteLinkedText = (text: string) => `‎${text}‎`

export const getVariableLabel = (variableType: QuoteVariableType): string => {
  return quoteVariableLabel[variableType]
}
